<template>
  <div class="card schedule-timeline">
    <div class="timeline-toolbar">
      <div>
        <h3 class="timeline-title">Assignment timeline</h3>
        <p class="timeline-subtitle">{{ visibleRangeLabel }}</p>
      </div>

      <div class="timeline-actions">
        <div class="timeline-legend" aria-label="Timeline legend">
          <span><span class="legend-swatch legend-assigned" />Assigned</span>
          <span><span class="legend-swatch legend-extension-waiting" />Extension waiting</span>
          <span>
            <span class="legend-swatch legend-extension-to-be-discussed" />Extension to be discussed
          </span>
          <span><span class="legend-swatch legend-available" />Available</span>
          <span><span class="legend-swatch legend-vacation" />Vacation</span>
          <span><span class="legend-swatch legend-leave" />Leave(parental/absence)</span>
          <span><span class="legend-swatch legend-unavailable" />Unavailable</span>
          <span><span class="legend-swatch legend-public-holiday" />Red day</span>
        </div>
        <a-button-group>
          <a-button
            aria-label="Show previous four weeks"
            @click="moveRange(-RANGE_SHIFT_WEEK_COUNT)"
          >
            <a-icon type="left" />
          </a-button>
          <a-button @click="resetRange">Today</a-button>
          <a-button aria-label="Show next four weeks" @click="moveRange(RANGE_SHIFT_WEEK_COUNT)">
            <a-icon type="right" />
          </a-button>
        </a-button-group>
      </div>
    </div>

    <empty-resource-table v-if="!hasScheduleUsers && !loadingTable" resource="week schedule" />

    <a-spin v-if="hasScheduleUsers || loadingTable" :spinning="loadingTable">
      <div class="timeline-scroll">
        <div class="timeline-grid timeline-header">
          <div class="person-heading" />
          <div
            v-for="week in weeks"
            :key="week.format('YYYY-MM-DD')"
            class="week-heading"
            :class="{ 'is-current-week': week.isSame(currentWeek, 'isoWeek') }"
          >
            <span class="week-number">W{{ week.isoWeek() }}</span>
            <span class="week-date">{{ getWorkWeekLabel(week) }}</span>
          </div>
        </div>

        <div v-for="user in schedulePage" :key="user.id" class="timeline-grid timeline-row">
          <div class="person-cell">
            <a-avatar :src="user.gravatar" :size="28" class="person-avatar">
              {{ getInitials(user.name) }}
            </a-avatar>
            <div class="person-copy">
              <div class="person-name-line">
                <router-link :to="{ name: 'user', params: { id: user.id } }">
                  {{ user.name }}
                </router-link>
              </div>
              <template v-if="shouldShowAvailabilityLabel(user)">
                <span class="role-meta">{{ getAvailabilityLabel(user) }}</span>
              </template>
            </div>
          </div>

          <div
            v-for="(week, weekIndex) in weeks"
            :key="`${user.id}-${week.format('YYYY-MM-DD')}`"
            class="timeline-cell"
            :class="getCellClasses(user, week)"
            :style="getCellStyle(user, week)"
          >
            <a-tooltip
              v-if="weekIndex === activeTimelineTooltipWeekIndex"
              :title="activeTimelineTooltipText"
              :visible="activeTimelineTooltipUserId === user.id"
              :mouse-enter-delay="0"
              :overlay-style="{ whiteSpace: 'pre-line', fontSize: '10px' }"
            >
              <div class="timeline-tooltip-anchor" :style="timelineTooltipAnchorStyle" />
            </a-tooltip>
            <div
              v-if="getAssignmentTooltip(user, week)"
              class="assignment-tooltip-target"
              role="img"
              :aria-label="getAssignmentTooltip(user, week)"
              @mouseenter="showAssignmentTooltip(user, week, weekIndex, $event)"
              @mouseleave="scheduleTimelineTooltipHide(user.id)"
            />
            <div
              v-for="absence in getAbsencesForWeek(user.id, week)"
              :key="absence.id"
              :class="getAbsenceClasses(absence, user)"
              :style="getAbsenceWeekStyle(absence, week)"
              role="img"
              :aria-label="getAbsenceTooltip(absence)"
              @mouseenter="showAbsenceTooltip(user.id, absence, weekIndex, $event)"
              @mouseleave="scheduleTimelineTooltipHide(user.id)"
            />
            <div
              v-for="publicHoliday in getPublicHolidaysForWeek(week)"
              :key="publicHoliday.format('YYYY-MM-DD')"
              class="public-holiday-segment"
              :style="getPublicHolidayWeekStyle(publicHoliday, week)"
              role="img"
              :aria-label="getPublicHolidayLabel(publicHoliday)"
            />

            <span
              v-if="
                shouldShowSegmentLabel(user, week, weekIndex) &&
                !shouldHideSegmentLabelForPendingExtension(user, week)
              "
              class="segment-label"
            >
              {{ getSegmentLabel(user, week) }}
            </span>
            <div
              v-for="extension in getPendingExtensionsForWeek(user.id, week)"
              :key="extension.id ?? `${extension.role.id}-${extension.end_date}`"
              class="pending-extension-segment"
              :class="{
                'is-waiting': extension.status === 'waiting',
                'is-to-be-discussed': extension.status === 'to_be_discussed',
                'is-approved': extension.status === 'approved',
              }"
              :style="getPendingExtensionStyle(extension, week)"
              role="img"
              :aria-label="getExtensionTooltip(extension)"
              @mouseenter="showExtensionTooltip(user.id, extension, weekIndex, $event)"
              @mouseleave="scheduleTimelineTooltipHide(user.id)"
            />
          </div>
        </div>
      </div>
    </a-spin>

    <div v-if="loadingTable || hasScheduleUsers" class="timeline-footer">
      <span class="result-count">{{ scheduleTotal }} consultants</span>
      <a-pagination
        :current="currentPage"
        :total="scheduleTotal"
        :show-size-changer="true"
        :page-size="pageSize"
        :page-size-options="['10', '25', '50', '100']"
        size="small"
        @change="(newPage, newPageSize) => handleGetSchedulePage(newPage, newPageSize)"
        @showSizeChange="(pageNumber, newSize) => handleGetSchedulePage(pageNumber, newSize)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import moment from "@/date";
import type { Moment } from "moment";
import Colors from "@/assets/scss/_colors.module.scss";
import EmptyResourceTable from "@/modules/common/_components/EmptyResourceTable.vue";
import { PaginationInterface } from "@/common/types";
import { PublicHolidayRangeInterface, ReportInterface, ReportType } from "@/modules/reports/types";
import { WeekSchedulePendingRoleExtensionInterface, WeekScheduleUserInterface } from "../types";
import {
  absenceOverlapsWorkWeek,
  getAbsenceWeekStyle,
  getAssignmentStartDate,
  getAvailabilityDate as getBaseAvailabilityDate,
  getCurrentDayPosition,
  getEmploymentStartDate,
  getPublicHolidayDatesForWeek,
  getPublicHolidayWeekStyle,
  getTimelineStatus as getBaseTimelineStatus,
  getWeekdayPosition,
  groupAbsenceReportsByUser,
  hasTimelineSegmentEndedBeforeDate as hasBaseTimelineSegmentEndedBeforeDate,
  isAbsenceWithinContinuingAssignment,
  isTransitionStatus,
  isUserAvailable as isBaseUserAvailable,
  isWaitingToStart,
  WORKDAYS_PER_WEEK,
  WORKDAY_WIDTH_PERCENT,
} from "../_utils/timeline";

const props = defineProps({
  schedulePage: { type: Array as () => Array<WeekScheduleUserInterface>, default: () => [] },
  absenceReports: { type: Array as () => Array<ReportInterface>, default: () => [] },
  publicHolidays: {
    type: Array as () => Array<PublicHolidayRangeInterface>,
    default: () => [],
  },
  pendingRoleExtensions: {
    type: Array as () => Array<WeekSchedulePendingRoleExtensionInterface>,
    default: () => [],
  },
  schedulePagination: { type: Object as () => PaginationInterface, default: undefined },
  loadingTable: { type: Boolean, default: false },
  currentPage: { type: Number, default: 1 },
  pageSize: { type: Number, default: 25 },
});

const emits = defineEmits(["get-schedule-page", "visible-years-change"]);

const VISIBLE_WEEK_COUNT = 10;
const RANGE_SHIFT_WEEK_COUNT = 4;
const EARLIEST_RANGE_OFFSET_YEARS = 1;
const TOOLTIP_HIDE_DELAY_MS = 50;
const SHORT_DATE_FORMAT = "MMM D";
const LONG_DATE_FORMAT = "MMM D, YYYY";
const currentWeek = moment().startOf("isoWeek");
const rangeStart = ref<Moment>(currentWeek.clone());
const activeTimelineTooltipUserId = ref<number | null>(null);
const activeTimelineTooltipText = ref<string>();
const activeTimelineTooltipWeekIndex = ref<number>(0);
const activeTimelineTooltipPosition = ref<{ left: number; top: number }>({ left: 50, top: 50 });
let timelineTooltipHideTimer: ReturnType<typeof setTimeout> | undefined;

const hasScheduleUsers = computed<boolean>(() => props.schedulePage.length > 0);
const scheduleTotal = computed<number>(
  () => props.schedulePagination?.total ?? props.schedulePage.length
);

const timelineTooltipAnchorStyle = computed<Record<string, string>>(() => ({
  left: `${activeTimelineTooltipPosition.value.left}%`,
  top: `${activeTimelineTooltipPosition.value.top}%`,
}));

const weeks = computed<Array<Moment>>(() =>
  Array.from({ length: VISIBLE_WEEK_COUNT }, (_, index) =>
    rangeStart.value.clone().add(index, "weeks")
  )
);

const pendingRoleExtensionsByUser = computed(
  (): Map<number, Array<WeekSchedulePendingRoleExtensionInterface>> => {
    const extensionsByUser = new Map<number, Array<WeekSchedulePendingRoleExtensionInterface>>();

    props.pendingRoleExtensions.forEach((extension) => {
      const extensions = extensionsByUser.get(extension.user_id) ?? [];
      extensions.push(extension);
      extensionsByUser.set(extension.user_id, extensions);
    });

    return extensionsByUser;
  }
);

const getPendingExtensionForRole = (
  user: WeekScheduleUserInterface
): WeekSchedulePendingRoleExtensionInterface | undefined =>
  (pendingRoleExtensionsByUser.value.get(user.id) ?? []).find(
    (extension) => extension.pending && extension.role.id === user.main_role?.id
  );

const getTimelineUser = (user: WeekScheduleUserInterface): WeekScheduleUserInterface => {
  const pendingExtension = getPendingExtensionForRole(user);
  if (!pendingExtension || !user.main_role) return user;

  return {
    ...user,
    main_role: {
      ...user.main_role,
      end_date: pendingExtension.end_date,
    },
  };
};

const getAvailabilityDate = (user: WeekScheduleUserInterface): Moment | undefined =>
  getBaseAvailabilityDate(getTimelineUser(user));

const getTimelineStatus = (user: WeekScheduleUserInterface, week: Moment) =>
  getBaseTimelineStatus(getTimelineUser(user), week);

const hasTimelineSegmentEndedBeforeDate = (
  user: WeekScheduleUserInterface,
  week: Moment
): boolean => hasBaseTimelineSegmentEndedBeforeDate(getTimelineUser(user), week);

const isUserAvailable = (user: WeekScheduleUserInterface): boolean =>
  isBaseUserAvailable(getTimelineUser(user));

const visibleRangeLabel = computed(() => {
  const rangeEnd = rangeStart.value
    .clone()
    .add(VISIBLE_WEEK_COUNT - 1, "weeks")
    .endOf("isoWeek");
  return `${rangeStart.value.format(SHORT_DATE_FORMAT)} – ${rangeEnd.format(LONG_DATE_FORMAT)}`;
});

const visibleYears = computed<Array<string>>(() => {
  const firstVisibleDate = weeks.value[0].clone().startOf("isoWeek");
  const lastVisibleDate = weeks.value[weeks.value.length - 1]
    .clone()
    .add(WORKDAYS_PER_WEEK - 1, "days");
  const firstYear = firstVisibleDate.year();
  const visibleYearCount = lastVisibleDate.year() - firstYear + 1;

  return Array.from({ length: visibleYearCount }, (_, index) => String(firstYear + index));
});

watch(
  visibleYears,
  (years) => {
    emits("visible-years-change", years);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (timelineTooltipHideTimer) clearTimeout(timelineTooltipHideTimer);
});

const absenceReportsByUser = computed<Map<number, Array<ReportInterface>>>(() =>
  groupAbsenceReportsByUser(props.absenceReports)
);
const publicHolidaysByWeek = computed<Map<string, Array<Moment>>>(
  () =>
    new Map(
      weeks.value.map((week) => [
        week.format("YYYY-MM-DD"),
        getPublicHolidayDatesForWeek(props.publicHolidays, week),
      ])
    )
);

const moveRange = (weeksToMove: number): void => {
  const nextRangeStart = rangeStart.value.clone().add(weeksToMove, "weeks");
  const earliestRangeStart = currentWeek.clone().subtract(EARLIEST_RANGE_OFFSET_YEARS, "year");

  if (nextRangeStart.isAfter(earliestRangeStart)) {
    rangeStart.value = nextRangeStart;
  }
};

const resetRange = (): void => {
  rangeStart.value = currentWeek.clone();
};

const getWorkWeekLabel = (week: Moment): string => {
  const weekStart = week.clone().startOf("isoWeek");
  const weekEnd = weekStart.clone().add(WORKDAYS_PER_WEEK - 1, "days");
  return `${weekStart.format(SHORT_DATE_FORMAT)} - ${weekEnd.format(SHORT_DATE_FORMAT)}`;
};

const getCellClasses = (user: WeekScheduleUserInterface, week: Moment): Record<string, boolean> => {
  const status = getTimelineStatus(user, week);
  return {
    [`is-${status}`]: true,
    "is-current-week": week.isSame(currentWeek, "isoWeek"),
  };
};

const getTransitionCellStyle = (
  transitionDate: Moment | undefined,
  week: Moment,
  initialColor: string,
  finalColor: string
): Record<string, string> => {
  if (!transitionDate) return {};

  const transitionPosition = getWeekdayPosition(transitionDate, week);
  return {
    "--transition-position": `${transitionPosition}%`,
    background:
      `linear-gradient(90deg, ${initialColor} ${transitionPosition}%, ` +
      `${finalColor} ${transitionPosition}%)`,
    color: Colors.white,
  };
};

const getCellStyle = (user: WeekScheduleUserInterface, week: Moment): Record<string, string> => {
  const status = getTimelineStatus(user, week);
  const currentDayStyle = week.isSame(currentWeek, "isoWeek")
    ? { "--current-day-position": `${getCurrentDayPosition(week)}%` }
    : {};

  switch (status) {
    case "assigned-to-available":
      return {
        ...currentDayStyle,
        ...getTransitionCellStyle(getAvailabilityDate(user), week, Colors.green, Colors.cyan),
      };
    case "available-to-assigned":
      return {
        ...currentDayStyle,
        ...getTransitionCellStyle(getAssignmentStartDate(user), week, Colors.cyan, Colors.green),
      };
    case "unavailable-to-available":
      return {
        ...currentDayStyle,
        ...getTransitionCellStyle(getEmploymentStartDate(user), week, Colors.grey, Colors.cyan),
      };
    default:
      return currentDayStyle;
  }
};

const getPendingExtensionStartDate = (
  extension: WeekSchedulePendingRoleExtensionInterface
): Moment => {
  const startDate = moment(extension.role.end_date).add(1, "day").startOf("day");

  while (startDate.isoWeekday() > WORKDAYS_PER_WEEK) {
    startDate.add(1, "day");
  }

  return startDate;
};

const getPendingExtensionsForWeek = (
  userId: number,
  week: Moment
): Array<WeekSchedulePendingRoleExtensionInterface> => {
  const weekStart = week.clone().startOf("isoWeek");
  const weekEnd = weekStart.clone().add(WORKDAYS_PER_WEEK - 1, "days");

  return (pendingRoleExtensionsByUser.value.get(userId) ?? []).filter((extension) => {
    if (!extension.pending) return false;

    const extensionStart = getPendingExtensionStartDate(extension);
    const extensionEnd = moment(extension.end_date).startOf("day");
    return (
      extensionEnd.isSameOrAfter(extensionStart, "day") &&
      extensionStart.isSameOrBefore(weekEnd, "day") &&
      extensionEnd.isSameOrAfter(weekStart, "day")
    );
  });
};

const hasUnapprovedPendingExtensionInWeek = (userId: number, week: Moment): boolean =>
  getPendingExtensionsForWeek(userId, week).some((extension) => extension.status !== "approved");

const shouldHideSegmentLabelForPendingExtension = (
  user: WeekScheduleUserInterface,
  week: Moment
): boolean =>
  hasUnapprovedPendingExtensionInWeek(user.id, week) &&
  getTimelineStatus(user, week) !== "assigned-to-available";

const getPendingExtensionStyle = (
  extension: WeekSchedulePendingRoleExtensionInterface,
  week: Moment
): Record<string, string> => {
  const weekStart = week.clone().startOf("isoWeek");
  const weekEnd = weekStart.clone().add(WORKDAYS_PER_WEEK - 1, "days");
  const extensionStart = getPendingExtensionStartDate(extension);
  const extensionEnd = moment(extension.end_date).startOf("day");
  const visibleStart = extensionStart.isAfter(weekStart, "day") ? extensionStart : weekStart;
  const visibleEnd = extensionEnd.isBefore(weekEnd, "day") ? extensionEnd : weekEnd;
  const startOffset = visibleStart.diff(weekStart, "days");
  const endOffset = visibleEnd.diff(weekStart, "days") + 1;

  return {
    left: `${startOffset * WORKDAY_WIDTH_PERCENT}%`,
    right: `${100 - endOffset * WORKDAY_WIDTH_PERCENT}%`,
  };
};

const shouldShowSegmentLabel = (
  user: WeekScheduleUserInterface,
  week: Moment,
  weekIndex: number
): boolean => {
  const status = getTimelineStatus(user, week);
  const previousWeek =
    weekIndex > 0 ? weeks.value[weekIndex - 1] : week.clone().subtract(1, "week");
  const previousStatus = getTimelineStatus(user, previousWeek);

  if (hasTimelineSegmentEndedBeforeDate(user, week)) return false;
  if (status === "unavailable") return false;
  if (isTransitionStatus(status)) return true;
  if (status === "available") {
    return previousStatus === "assigned" || previousStatus === "unavailable";
  }
  if (weekIndex === 0) return true;
  return status !== previousStatus && !isTransitionStatus(previousStatus);
};

const formatAvailableFromLabel = (availableFrom?: Moment): string =>
  availableFrom?.isAfter(moment(), "day")
    ? `Available · ${availableFrom.format(SHORT_DATE_FORMAT)}`
    : "";

const getSegmentLabel = (user: WeekScheduleUserInterface, week: Moment): string => {
  const status = getTimelineStatus(user, week);
  const previousStatus = getTimelineStatus(user, week.clone().subtract(1, "week"));
  const role = user.main_role?.client_name || "Assigned";

  if (status === "assigned-to-available") {
    return formatAvailableFromLabel(getAvailabilityDate(user));
  }

  if (status === "available-to-assigned") {
    const assignmentStartDate = getAssignmentStartDate(user);
    return assignmentStartDate?.isSameOrBefore(moment(), "day")
      ? role
      : `${role} · ${assignmentStartDate?.format(SHORT_DATE_FORMAT)}`;
  }

  if (status === "unavailable-to-available") {
    return formatAvailableFromLabel(getEmploymentStartDate(user));
  }

  if (
    status === "available" &&
    (previousStatus === "assigned" || previousStatus === "unavailable")
  ) {
    const availableFrom =
      previousStatus === "assigned" ? getAvailabilityDate(user) : getEmploymentStartDate(user);
    return formatAvailableFromLabel(availableFrom);
  }

  if (status === "assigned") {
    const assignmentStartDate = getAssignmentStartDate(user);
    return assignmentStartDate?.isSameOrBefore(moment(), "day")
      ? role
      : `${role} · ${assignmentStartDate?.format(SHORT_DATE_FORMAT)}`;
  }

  return "";
};

const getAbsencesForWeek = (userId: number, week: Moment): Array<ReportInterface> =>
  (absenceReportsByUser.value.get(userId) || []).filter((absence) =>
    absenceOverlapsWorkWeek(absence, week)
  );

const getPublicHolidaysForWeek = (week: Moment): Array<Moment> =>
  publicHolidaysByWeek.value.get(week.format("YYYY-MM-DD")) ?? [];

const getPublicHolidayLabel = (publicHoliday: Moment): string =>
  `Red day · ${publicHoliday.format(LONG_DATE_FORMAT)}`;

const getAbsenceLabel = (type?: string): string => {
  switch (type) {
    case ReportType.VACATION:
      return "Vacation";
    case ReportType.PARENTAL:
      return "Parental leave";
    case ReportType.ABSENCE:
      return "Leave of absence";
    default:
      return "Absence";
  }
};

const getAbsenceClasses = (
  absence: ReportInterface,
  user: WeekScheduleUserInterface
): Record<string, boolean> => ({
  "absence-segment": true,
  "is-vacation": absence.type === ReportType.VACATION,
  "is-leave": absence.type === ReportType.PARENTAL || absence.type === ReportType.ABSENCE,
  "is-during-assignment": isAbsenceWithinContinuingAssignment(
    absence,
    getTimelineUser(user).main_role
  ),
});

const getAbsenceTooltip = (absence: ReportInterface): string => {
  const label = getAbsenceLabel(absence.type);
  const start = moment(absence.start_date).format(LONG_DATE_FORMAT);
  const end = moment(absence.end_date || absence.start_date).format(LONG_DATE_FORMAT);
  return start === end ? `${label}\n${start}` : `${label}\n${start} – ${end}`;
};

const formatWorkload = (workload: number): string => `${Math.round(workload * 100)}%`;

const getAssignmentTooltip = (
  user: WeekScheduleUserInterface,
  week: Moment
): string | undefined => {
  const status = getTimelineStatus(user, week);
  const containsAssignment =
    status === "assigned" ||
    status === "assigned-to-available" ||
    status === "available-to-assigned";
  const timelineRole = getTimelineUser(user).main_role;
  if (!containsAssignment || !timelineRole) return undefined;

  const start = getAssignmentStartDate(user)?.format(LONG_DATE_FORMAT) || "Unknown start date";
  const end = timelineRole.end_date
    ? moment(timelineRole.end_date).format(LONG_DATE_FORMAT)
    : "No end date";

  return [
    timelineRole.title,
    `${timelineRole.client_name} · ${formatWorkload(timelineRole.workload)}`,
    `${start} – ${end}`,
  ].join("\n");
};

const extensionStatusLabels: Record<WeekSchedulePendingRoleExtensionInterface["status"], string> = {
  waiting: "Waiting",
  to_be_discussed: "To be discussed",
  approved: "Approved",
};

const getExtensionTooltip = (extension: WeekSchedulePendingRoleExtensionInterface): string => {
  const start = getPendingExtensionStartDate(extension).format(LONG_DATE_FORMAT);
  const end = moment(extension.end_date).format(LONG_DATE_FORMAT);

  return [
    extension.role.title,
    `${extension.role.project.name} · ${formatWorkload(extension.workload)}`,
    `Extension · ${extensionStatusLabels[extension.status]}`,
    `${start} – ${end}`,
  ].join("\n");
};

const setTimelineTooltipAnchor = (weekIndex: number, event: MouseEvent): void => {
  const timelineCell = (event.currentTarget as HTMLElement).closest(
    ".timeline-cell"
  ) as HTMLElement | null;
  if (!timelineCell) return;

  const bounds = timelineCell.getBoundingClientRect();
  activeTimelineTooltipWeekIndex.value = weekIndex;
  activeTimelineTooltipPosition.value = {
    left: Math.min(Math.max(((event.clientX - bounds.left) / bounds.width) * 100, 0), 100),
    top: Math.min(Math.max(((event.clientY - bounds.top) / bounds.height) * 100, 0), 100),
  };
};

const showTimelineTooltip = (
  userId: number,
  tooltip: string,
  weekIndex: number,
  event: MouseEvent
): void => {
  if (timelineTooltipHideTimer) clearTimeout(timelineTooltipHideTimer);
  if (activeTimelineTooltipUserId.value !== userId || activeTimelineTooltipText.value !== tooltip) {
    setTimelineTooltipAnchor(weekIndex, event);
  }

  activeTimelineTooltipUserId.value = userId;
  activeTimelineTooltipText.value = tooltip;
};

const showAssignmentTooltip = (
  user: WeekScheduleUserInterface,
  week: Moment,
  weekIndex: number,
  event: MouseEvent
): void => {
  const tooltip = getAssignmentTooltip(user, week);
  if (!tooltip) return;

  showTimelineTooltip(user.id, tooltip, weekIndex, event);
};

const showAbsenceTooltip = (
  userId: number,
  absence: ReportInterface,
  weekIndex: number,
  event: MouseEvent
): void => {
  showTimelineTooltip(userId, getAbsenceTooltip(absence), weekIndex, event);
};

const showExtensionTooltip = (
  userId: number,
  extension: WeekSchedulePendingRoleExtensionInterface,
  weekIndex: number,
  event: MouseEvent
): void => {
  showTimelineTooltip(userId, getExtensionTooltip(extension), weekIndex, event);
};

const scheduleTimelineTooltipHide = (userId: number): void => {
  if (timelineTooltipHideTimer) clearTimeout(timelineTooltipHideTimer);

  timelineTooltipHideTimer = setTimeout(() => {
    if (activeTimelineTooltipUserId.value !== userId) return;
    activeTimelineTooltipUserId.value = null;
    activeTimelineTooltipText.value = undefined;
  }, TOOLTIP_HIDE_DELAY_MS);
};

const shouldShowAvailabilityLabel = (user: WeekScheduleUserInterface): boolean =>
  isWaitingToStart(user) || !isUserAvailable(user);

const getAvailabilityLabel = (user: WeekScheduleUserInterface): string => {
  const employmentStarts = getEmploymentStartDate(user);
  if (isWaitingToStart(user) && employmentStarts) {
    return `Available ${employmentStarts.format(LONG_DATE_FORMAT)}`;
  }

  const availableFrom = getAvailabilityDate(user);
  return availableFrom ? `Available ${availableFrom.format(LONG_DATE_FORMAT)}` : "No end date";
};

const getInitials = (name: string): string =>
  name
    .split(" ")
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

const handleGetSchedulePage = (page: number, pageSize: number): void => {
  emits("get-schedule-page", { page, pageSize });
};
</script>

<style scoped lang="scss">
@use "sass:color";
@use "@/assets/scss/colors.module" as colors;

$pending-extension-accent: color.adjust(colors.$green, $lightness: -10%);
$pending-extension-fill: rgba(colors.$green, 0.3);

.schedule-timeline {
  padding: 0;
  overflow: hidden;
}

.timeline-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 10px 20px;
  border-bottom: 1px solid colors.$grey_light;
}

.timeline-title {
  margin: 0 0 2px;
  font-size: 16px;
}

.timeline-subtitle {
  margin: 0;
  color: colors.$grey_dark;
  font-size: 12px;
}

.timeline-actions,
.timeline-legend,
.timeline-footer {
  display: flex;
  align-items: center;
}

.timeline-actions {
  gap: 22px;
}

.timeline-legend {
  display: grid;
  grid-template-columns: repeat(4, max-content);
  gap: 6px 14px;
  color: colors.$grey_darker;
  font-size: 12px;
}

.timeline-legend > span {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.legend-swatch {
  width: 9px;
  height: 9px;
  margin-right: 5px;
  border-radius: 3px;
}

.legend-assigned {
  background: colors.$green;
}

.legend-extension-waiting,
.legend-extension-to-be-discussed {
  background: linear-gradient($pending-extension-fill, $pending-extension-fill), colors.$white;
}

.legend-extension-waiting {
  background:
    repeating-linear-gradient(
      135deg,
      transparent 0,
      transparent 4px,
      $pending-extension-accent 5px
    ),
    linear-gradient($pending-extension-fill, $pending-extension-fill), colors.$white;
}

.legend-extension-to-be-discussed {
  background:
    repeating-linear-gradient(
      135deg,
      transparent 0,
      transparent 2px,
      $pending-extension-accent 5px
    ),
    linear-gradient($pending-extension-fill, $pending-extension-fill), colors.$white;
}

.legend-available {
  background: colors.$cyan;
}

.legend-unavailable {
  background: colors.$grey;
}

.legend-vacation,
.absence-segment.is-vacation {
  background: colors.$lemon;
}

.legend-leave,
.absence-segment.is-leave {
  background: colors.$solitude;
}

.legend-public-holiday,
.public-holiday-segment {
  background: colors.$red;
}

.timeline-grid {
  display: grid;
  grid-template-columns: 280px repeat(10, minmax(0, 1fr));
  width: 100%;
}

.timeline-scroll {
  max-height: 75vh;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.timeline-header {
  position: sticky;
  top: 0;
  z-index: 4;
  background: colors.$grey_lightest;
  border-bottom: 1px solid colors.$grey_light;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.person-heading,
.week-heading {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 50px;
}

.person-heading {
  position: sticky;
  left: 0;
  z-index: 5;
  padding: 0 18px;
  background: colors.$grey_lightest;
  color: colors.$grey_darker;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  box-shadow: 5px 0 10px -10px colors.$black;
}

.week-heading {
  align-items: center;
  border-left: 1px solid colors.$grey_light;
}

.week-number {
  color: colors.$grey_darker;
  font-size: 11px;
  font-weight: 700;
}

.week-date {
  color: colors.$grey_dark;
  font-size: 10px;
}

.timeline-row {
  height: 46px;
  box-sizing: border-box;
  border-bottom: 1px solid colors.$grey_light;
}

.timeline-row:last-child {
  border-bottom: 0;
}

.person-cell {
  position: sticky;
  left: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 2px 10px;
  background: colors.$white;
  box-shadow: 5px 0 10px -10px colors.$black;
  overflow: hidden;
}

.person-avatar {
  flex: 0 0 auto;
  margin-right: 8px;
  background: colors.$cyan;
}

.person-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.person-name-line {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
}

.person-name-line a,
.role-meta {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.role-meta {
  color: colors.$grey_dark;
  font-size: 10px;
  line-height: 12px;
}

.timeline-cell {
  position: relative;
  display: flex;
  min-width: 0;
  align-items: center;
  margin: 5px 0;
  padding: 0 8px;
}

.timeline-cell::before {
  position: absolute;
  top: -5px;
  right: 0;
  bottom: -6px;
  z-index: 3;
  border-right: 1px dotted colors.$grey_light;
  content: "";
  pointer-events: none;
}

.timeline-cell.is-assigned-to-available .segment-label,
.timeline-cell.is-available-to-assigned .segment-label,
.timeline-cell.is-unavailable-to-available .segment-label {
  position: absolute;
  left: calc(var(--transition-position, 50%) + 8px);
}

.timeline-cell .segment-label {
  color: colors.$white;
}

.assignment-tooltip-target {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.timeline-tooltip-anchor {
  position: absolute;
  width: 1px;
  height: 1px;
  pointer-events: none;
}

.absence-segment,
.public-holiday-segment {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 1;
}

.absence-segment.is-during-assignment {
  margin: 1px 0;
}

.pending-extension-segment {
  position: absolute;
  top: 0;
  bottom: 0;
  min-width: 0;
  background: linear-gradient($pending-extension-fill, $pending-extension-fill), colors.$white;
}

.pending-extension-segment.is-waiting {
  background:
    repeating-linear-gradient(
      135deg,
      transparent 0,
      transparent 7px,
      $pending-extension-accent 8px
    ),
    linear-gradient($pending-extension-fill, $pending-extension-fill), colors.$white;
}

.pending-extension-segment.is-to-be-discussed {
  background:
    repeating-linear-gradient(
      135deg,
      transparent 0,
      transparent 4px,
      $pending-extension-accent 8px
    ),
    linear-gradient($pending-extension-fill, $pending-extension-fill), colors.$white;
}

.pending-extension-segment.is-approved {
  background: transparent;
}

.pending-extension-segment.is-waiting,
.pending-extension-segment.is-to-be-discussed {
  background-attachment: fixed, scroll;
}

.timeline-cell.is-assigned {
  background: colors.$green;
}

.timeline-cell.is-available {
  background: colors.$cyan;
}

.timeline-cell.is-assigned,
.timeline-cell.is-available {
  color: colors.$white;
}

.timeline-cell.is-unavailable {
  background: colors.$grey;
  color: colors.$grey_darker;
}

.timeline-cell.is-current-week::after,
.week-heading.is-current-week::after {
  position: absolute;
  width: 1px;
  background: colors.$orange;
  content: "";
  opacity: 0.8;
  pointer-events: none;
}

.timeline-cell.is-current-week::after {
  top: -5px;
  bottom: -6px;
  z-index: 3;
  left: var(--current-day-position, 0%);
}

.week-heading.is-current-week {
  position: relative;
  background: #fff8f4;
}

.week-heading.is-current-week::after {
  top: auto;
  right: 0;
  bottom: -1px;
  left: 0;
  width: auto;
  height: 2px;
}

.timeline-row:last-child .timeline-cell.is-current-week::after {
  bottom: -5px;
}

.timeline-row:last-child .timeline-cell::before {
  bottom: -5px;
}

.segment-label {
  position: relative;
  z-index: 2;
  font-size: 11px;
  font-weight: 500;
  pointer-events: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.timeline-footer {
  justify-content: space-between;
  padding: 14px 20px;
  border-top: 1px solid colors.$grey_light;
}

.result-count {
  color: colors.$grey_dark;
  font-size: 12px;
}

@media (max-width: 900px) {
  .timeline-toolbar,
  .timeline-actions {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }
}
</style>
